import type { ProductListQuery } from '@/api/handlers/resources/products/requests';

/**
 * Centralised TanStack Query keys for the Products resource. Sharing the keys
 * here keeps mutations and queries from drifting out of step when one is
 * invalidated and the other is read.
 *
 * The list key folds the full filter + pagination shape in, so any change to
 * `page` / `search` / `categoryId` / … is a distinct cache entry — switching a
 * filter re-fetches rather than reading stale rows for the wrong query.
 */
export const ProductQueryKeys = {
    all: ['products'] as const,
    lists: () => [...ProductQueryKeys.all, 'list'] as const,
    list: (query: ProductListQuery) =>
        [...ProductQueryKeys.lists(), query] as const,
    details: () => [...ProductQueryKeys.all, 'detail'] as const,
    /**
     * One product's detail, under the branch whose stock it counts
     * (bos-app#119). Without a branch the key is the prefix of every branch's,
     * so invalidating or removing it reaches the product under every branch,
     * which is what an edit or a delete needs.
     */
    detail: (id: string, branchId: string | null = null) =>
        branchId === null
            ? ([...ProductQueryKeys.details(), id] as const)
            : ([...ProductQueryKeys.details(), id, branchId] as const),
    /** Every page of Barcode issues, under every branch (bos-api API-207). */
    barcodeIssueLists: () =>
        [...ProductQueryKeys.all, 'barcodeIssues'] as const,
    /**
     * One page of Barcode issues; `branchId: null` is the caller's own scope.
     * `session` is the signed-in organisation and user (`sessionScopeOf`), so a
     * sign-in to another organisation in another tab, which changes the shared
     * bearer but not this tab's cache, never reads the last one's rows (review
     * of #241, finding 7). The app-wide session generation is still an open
     * migration (`.agents/frontend.md`, §10).
     */
    barcodeIssues: (
        session: string | null,
        branchId: string | null,
        page: number,
        pageSize: number,
    ) =>
        [
            ...ProductQueryKeys.barcodeIssueLists(),
            session,
            branchId,
            page,
            pageSize,
        ] as const,
};

/**
 * The signed-in organisation and user a session-scoped key carries:
 * `<orgId>/<userId>`, or `null` with nobody signed in. Not the bearer, which
 * rotates on every refresh.
 */
export const sessionScopeOf = (
    user: { id: string; orgId: string } | null,
): string | null => (user === null ? null : `${user.orgId}/${user.id}`);
